import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, PieChart, Users, DollarSign, Activity, BarChart3, Download } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle, btnStyle, statCard } from '../theme';

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const todayISO = () => new Date().toISOString().slice(0,10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };

export default function AnalyticsHubPage({ API_BASE }) {
    const [tab, setTab] = useState('profit');

    const tabBtn = (k, Icon, label) => (
        <button onClick={()=>setTab(k)} style={{padding:'9px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.82rem',
            backgroundColor: tab===k ? theme.primary : '#e9ecef', color: tab===k ? 'white' : '#555',
            display:'inline-flex', alignItems:'center', gap:'6px'}}>
            <Icon size={13}/>{label}
        </button>
    );

    return (
        <div>
            <div style={{marginBottom:'16px'}}>
                <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Analytics Hub</h2>
                <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>Business intelligence beyond the basic dashboards</p>
            </div>
            <div style={{display:'flex', gap:'6px', marginBottom:'18px', flexWrap:'wrap'}}>
                {tabBtn('profit',      TrendingUp,  'Profit Center')}
                {tabBtn('payments',    PieChart,    'Payment Trends')}
                {tabBtn('ltv',         Users,       'Patient LTV')}
                {tabBtn('forecast',    DollarSign,  'Cash Flow Forecast')}
                {tabBtn('doctors',     Activity,    'Doctor / Branch')}
                {tabBtn('abc',         BarChart3,   'ABC Analysis')}
            </div>

            {tab==='profit'   && <ProfitTab   API_BASE={API_BASE}/>}
            {tab==='payments' && <PaymentsTab API_BASE={API_BASE}/>}
            {tab==='ltv'      && <LtvTab      API_BASE={API_BASE}/>}
            {tab==='forecast' && <ForecastTab API_BASE={API_BASE}/>}
            {tab==='doctors'  && <DoctorsTab  API_BASE={API_BASE}/>}
            {tab==='abc'      && <AbcTab      API_BASE={API_BASE}/>}
        </div>
    );
}

// ── Profit Center ─────────────────────────────────────────────────────
function ProfitTab({ API_BASE }) {
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const load = async () => {
        try { const r = await axios.get(`${API_BASE}/analytics-engine/profit-center?from=${from}&to=${to}`); setData(r.data); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    return (
        <div>
            <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onRun={load}/>
            {data && (<>
                <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'14px'}}>
                    <Kpi l="Revenue" v={fmtINR(data.totals.revenue)} c={theme.success}/>
                    <Kpi l="Est. Cost" v={fmtINR(data.totals.estCost)} c={theme.danger}/>
                    <Kpi l="Gross Profit" v={fmtINR(data.totals.grossProfit)} c={theme.primary}/>
                    <Kpi l="Margin %" v={`${data.totals.marginPct}%`} c={data.totals.marginPct>=50?theme.success:data.totals.marginPct>=30?theme.warning:theme.danger}/>
                </div>
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Profit by Treatment / Item ({data.rows.length})</h3>
                    <p style={{fontSize:'0.76rem', color:theme.textMuted, margin:'0 0 10px'}}>Cost estimate uses 30% of revenue for treatments and 60% for medicines. Override in future by wiring real costs.</p>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Item</th><th style={thStyle}>Type</th><th style={thStyle}>Units</th>
                            <th style={thStyle}>Revenue</th><th style={thStyle}>Est. Cost</th><th style={thStyle}>Gross</th><th style={thStyle}>Margin</th>
                        </tr></thead>
                        <tbody>
                            {data.rows.slice(0,50).map((r,i) => (
                                <tr key={i}>
                                    <td style={tdStyle}><strong>{r.name}</strong></td>
                                    <td style={tdStyle}>{r.itemType}</td>
                                    <td style={tdStyle}>{r.count}</td>
                                    <td style={tdStyle}>{fmtINR(r.revenue)}</td>
                                    <td style={tdStyle}><span style={{color:theme.danger}}>{fmtINR(r.estCost)}</span></td>
                                    <td style={tdStyle}><strong style={{color:theme.primary}}>{fmtINR(r.grossProfit)}</strong></td>
                                    <td style={tdStyle}><span style={{color: r.marginPct>=50?theme.success:r.marginPct>=30?theme.warning:theme.danger, fontWeight:'700'}}>{r.marginPct}%</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>)}
        </div>
    );
}

// ── Payment Trends ────────────────────────────────────────────────────
function PaymentsTab({ API_BASE }) {
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(todayISO());
    const [granularity, setGranularity] = useState('day');
    const [data, setData] = useState(null);
    const load = async () => {
        try { const r = await axios.get(`${API_BASE}/analytics-engine/payment-trends?from=${from}&to=${to}&granularity=${granularity}`); setData(r.data); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const maxTotal = data?.rows?.reduce((m,r) => Math.max(m, r.total), 0) || 1;
    const modes = ['cash','card','upi','cheque','bank-transfer','insurance','other'];
    const modeColors = { cash:'#10b981', card:'#3b82f6', upi:'#8b5cf6', cheque:'#f59e0b', 'bank-transfer':'#06b6d4', insurance:'#ec4899', other:'#6b7280' };

    return (
        <div>
            <div style={{display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px', flexWrap:'wrap'}}>
                <div><label style={labelStyle}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{...inputStyle, width:'150px', marginBottom:0}}/></div>
                <div><label style={labelStyle}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{...inputStyle, width:'150px', marginBottom:0}}/></div>
                <div><label style={labelStyle}>Granularity</label>
                    <select value={granularity} onChange={e=>setGranularity(e.target.value)} style={{...inputStyle, width:'120px', marginBottom:0}}>
                        <option value="day">Daily</option>
                        <option value="month">Monthly</option>
                    </select>
                </div>
                <button onClick={load} style={{...btnStyle, marginTop:0, padding:'8px 16px', fontSize:'0.85rem'}}>Refresh</button>
            </div>
            {data && (<>
                <div style={{display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:'8px', marginBottom:'14px'}}>
                    <Kpi l="Total" v={fmtINR(data.totals.total)} c={theme.primary}/>
                    {modes.map(m => data.totals[m] > 0 && <Kpi key={m} l={m} v={fmtINR(data.totals[m])} c={modeColors[m]}/>)}
                </div>
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>{granularity==='day' ? 'Daily' : 'Monthly'} Trend</h3>
                    <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
                        {data.rows.map(r => (
                            <div key={r.period} style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                <div style={{width:90, fontSize:'0.76rem', color:theme.textMuted, fontFamily:'monospace'}}>{r.period}</div>
                                <div style={{flex:1, display:'flex', height:24, borderRadius:4, overflow:'hidden', backgroundColor:'#f1f5f9'}}>
                                    {modes.map(m => r[m] > 0 && <div key={m} title={`${m}: ${fmtINR(r[m])}`} style={{width:`${(r[m]/maxTotal)*100}%`, backgroundColor:modeColors[m]}}/>)}
                                </div>
                                <div style={{width:100, textAlign:'right', fontWeight:'700', fontSize:'0.82rem', color:theme.navy}}>{fmtINR(r.total)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </>)}
        </div>
    );
}

// ── Patient LTV ───────────────────────────────────────────────────────
function LtvTab({ API_BASE }) {
    const [data, setData] = useState(null);
    useEffect(() => { axios.get(`${API_BASE}/analytics-engine/patient-ltv?limit=50`).then(r=>setData(r.data)).catch(()=>{}); }, [API_BASE]);
    if (!data) return <div style={{padding:20}}>Loading…</div>;

    const segColor = { vip:{bg:'#fef3c7', c:'#92400e'}, loyal:{bg:'#dbeafe', c:'#1e40af'}, regular:{bg:'#e5e7eb', c:'#374151'}, new:{bg:'#d1fae5', c:'#065f46'}, dormant:{bg:'#fee2e2', c:'#991b1b'} };

    return (
        <div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'14px'}}>
                <Kpi l="Patients" v={data.totals.patients}/>
                <Kpi l="Total Lifetime Value" v={fmtINR(data.totals.totalValue)} c={theme.primary}/>
                <Kpi l="Avg LTV" v={fmtINR(data.totals.avgLtv)} c={theme.success}/>
            </div>
            <div style={{...cardStyle, marginBottom:'14px'}}>
                <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Patient Segments</h3>
                <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                    {Object.entries(data.segments).map(([k,v]) => {
                        const col = segColor[k] || {bg:'#f1f5f9', c:'#475569'};
                        return (
                            <div key={k} style={{padding:'10px 14px', borderRadius:'10px', backgroundColor:col.bg, minWidth:100}}>
                                <div style={{fontSize:'0.72rem', fontWeight:'700', color:col.c, textTransform:'uppercase'}}>{k}</div>
                                <div style={{fontSize:'1.2rem', fontWeight:'700', color:col.c}}>{v}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div style={cardStyle}>
                <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Top 50 Patients by Lifetime Value</h3>
                <table style={{width:'100%', borderCollapse:'collapse'}}>
                    <thead><tr>
                        <th style={thStyle}>Name</th><th style={thStyle}>UHID</th><th style={thStyle}>Segment</th>
                        <th style={thStyle}>Visits</th><th style={thStyle}>Avg/Visit</th><th style={thStyle}>Lifetime</th><th style={thStyle}>Last Visit</th>
                    </tr></thead>
                    <tbody>
                        {data.top.map(r => {
                            const col = segColor[r.segment] || {bg:'#f1f5f9', c:'#475569'};
                            return (
                                <tr key={r.patientId}>
                                    <td style={tdStyle}><strong>{r.name}</strong><div style={{fontSize:'0.72rem', color:theme.textMuted}}>{r.phone}</div></td>
                                    <td style={tdStyle}><code style={{fontSize:'0.76rem'}}>{r.uhid}</code></td>
                                    <td style={tdStyle}><span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:col.bg, color:col.c, textTransform:'uppercase'}}>{r.segment}</span></td>
                                    <td style={tdStyle}>{r.visits}</td>
                                    <td style={tdStyle}>{fmtINR(r.avgSpend)}</td>
                                    <td style={tdStyle}><strong style={{color:theme.primary}}>{fmtINR(r.totalSpend)}</strong></td>
                                    <td style={tdStyle}>{fmtDate(r.lastDate)} <span style={{color:theme.textMuted, fontSize:'0.72rem'}}>({r.recencyDays}d ago)</span></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Cash Flow Forecast ────────────────────────────────────────────────
function ForecastTab({ API_BASE }) {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const load = async () => { try { const r = await axios.get(`${API_BASE}/analytics-engine/cash-flow-forecast?days=${days}`); setData(r.data); } catch {} };
    useEffect(() => { load(); }, []); // eslint-disable-line

    if (!data) return <div style={{padding:20}}>Loading…</div>;
    const maxT = data.rows.reduce((m,r)=>Math.max(m, r.total), 1);

    return (
        <div>
            <div style={{display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px'}}>
                <div><label style={labelStyle}>Horizon (days)</label>
                    <select value={days} onChange={e=>setDays(Number(e.target.value))} style={{...inputStyle, width:'120px', marginBottom:0}}>
                        <option value={30}>30</option>
                        <option value={60}>60</option>
                        <option value={90}>90</option>
                    </select>
                </div>
                <button onClick={load} style={{...btnStyle, marginTop:0, padding:'8px 16px', fontSize:'0.85rem'}}>Refresh</button>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'14px'}}>
                <Kpi l="Avg Daily (hist.)" v={fmtINR(data.avgDailyHistoric)}/>
                <Kpi l="Outstanding Balance" v={fmtINR(data.outstandingBalance)} c={theme.warning}/>
                <Kpi l="EMI Scheduled" v={fmtINR(data.totals.emi)} c={theme.info}/>
                <Kpi l={`Projected ${days}d`} v={fmtINR(data.totals.total)} c={theme.primary}/>
            </div>
            <div style={cardStyle}>
                <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Daily Projection</h3>
                <div style={{maxHeight:'500px', overflowY:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.82rem'}}>
                        <thead style={{position:'sticky', top:0, backgroundColor:'white'}}><tr>
                            <th style={thStyle}>Date</th><th style={thStyle}>Baseline</th><th style={thStyle}>EMI Due</th>
                            <th style={thStyle}>Outstanding Collect</th><th style={thStyle}>Projected Total</th>
                            <th style={thStyle}>Visual</th>
                        </tr></thead>
                        <tbody>
                            {data.rows.filter(r => r.total > 0).map(r => (
                                <tr key={r.date}>
                                    <td style={tdStyle}>{r.date}</td>
                                    <td style={tdStyle}>{fmtINR(r.baseline)}</td>
                                    <td style={tdStyle}>{r.emi > 0 ? <strong style={{color:theme.info}}>{fmtINR(r.emi)}</strong> : '—'}</td>
                                    <td style={tdStyle}>{r.outstandingCollect > 0 ? <strong style={{color:theme.warning}}>{fmtINR(r.outstandingCollect)}</strong> : '—'}</td>
                                    <td style={tdStyle}><strong style={{color:theme.primary}}>{fmtINR(r.total)}</strong></td>
                                    <td style={tdStyle}><div style={{height:12, width:`${(r.total/maxT)*100}%`, backgroundColor:theme.primary, borderRadius:2, minWidth:2}}/></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Doctor / Branch ───────────────────────────────────────────────────
function DoctorsTab({ API_BASE }) {
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(todayISO());
    const [docs, setDocs] = useState(null);
    const [branches, setBranches] = useState(null);

    const load = async () => {
        try {
            const [d, b] = await Promise.all([
                axios.get(`${API_BASE}/analytics-engine/doctor-revenue?from=${from}&to=${to}`),
                axios.get(`${API_BASE}/analytics-engine/branch-revenue?from=${from}&to=${to}`),
            ]);
            setDocs(d.data); setBranches(b.data);
        } catch {}
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    return (
        <div>
            <DateBar from={from} to={to} setFrom={setFrom} setTo={setTo} onRun={load}/>
            {docs && (
                <div style={{...cardStyle, marginBottom:'14px'}}>
                    <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Doctor Revenue ({docs.rows.length})</h3>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Doctor</th><th style={thStyle}>Bills</th>
                            <th style={thStyle}>Patients</th><th style={thStyle}>Line Items</th>
                            <th style={thStyle}>Avg Bill</th><th style={thStyle}>Revenue</th>
                        </tr></thead>
                        <tbody>
                            {docs.rows.map((r,i) => (
                                <tr key={i}>
                                    <td style={tdStyle}><strong>{r.name}</strong></td>
                                    <td style={tdStyle}>{r.bills}</td>
                                    <td style={tdStyle}>{r.patients}</td>
                                    <td style={tdStyle}>{r.lineCount}</td>
                                    <td style={tdStyle}>{fmtINR(r.avgBillValue)}</td>
                                    <td style={tdStyle}><strong style={{color:theme.primary}}>{fmtINR(r.revenue)}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {branches && branches.rows.length > 1 && (
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Branch Revenue</h3>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Branch</th><th style={thStyle}>Bills</th>
                            <th style={thStyle}>Avg Bill</th><th style={thStyle}>Revenue</th>
                        </tr></thead>
                        <tbody>
                            {branches.rows.map((r,i) => (
                                <tr key={i}>
                                    <td style={tdStyle}><strong>{r.name}</strong></td>
                                    <td style={tdStyle}>{r.bills}</td>
                                    <td style={tdStyle}>{fmtINR(r.avgBillValue)}</td>
                                    <td style={tdStyle}><strong style={{color:theme.primary}}>{fmtINR(r.revenue)}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── ABC Analysis ──────────────────────────────────────────────────────
function AbcTab({ API_BASE }) {
    const [entity, setEntity] = useState('treatments');
    const [from, setFrom] = useState(daysAgo(90));
    const [to, setTo] = useState(todayISO());
    const [data, setData] = useState(null);

    const load = async () => {
        try { const r = await axios.get(`${API_BASE}/analytics-engine/abc?entity=${entity}&from=${from}&to=${to}`); setData(r.data); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };
    useEffect(() => { load(); }, [entity]); // eslint-disable-line

    return (
        <div>
            <div style={{display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px'}}>
                <div><label style={labelStyle}>Entity</label>
                    <select value={entity} onChange={e=>setEntity(e.target.value)} style={{...inputStyle, marginBottom:0, width:'160px'}}>
                        <option value="treatments">Treatments</option>
                        <option value="medicines">Medicines (pharmacy)</option>
                    </select>
                </div>
                <div><label style={labelStyle}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{...inputStyle, marginBottom:0, width:'150px'}}/></div>
                <div><label style={labelStyle}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{...inputStyle, marginBottom:0, width:'150px'}}/></div>
                <button onClick={load} style={{...btnStyle, marginTop:0, padding:'8px 16px', fontSize:'0.85rem'}}>Run</button>
            </div>
            {data && (<>
                <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'14px'}}>
                    <Kpi l="Total Items" v={data.summary.total}/>
                    <Kpi l="A (top 80%)" v={data.summary.A} c={theme.success}/>
                    <Kpi l="B (next 15%)" v={data.summary.B} c={theme.warning}/>
                    <Kpi l="C (bottom 5%)" v={data.summary.C} c={theme.danger}/>
                </div>
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 10px', color:theme.navy, fontSize:'1rem'}}>Pareto Ranking — {entity}</h3>
                    <p style={{fontSize:'0.78rem', color:theme.textMuted, margin:'0 0 10px'}}>Class A items drive 80% of revenue — focus your attention there.</p>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead><tr>
                            <th style={thStyle}>Rank</th><th style={thStyle}>Item</th>
                            <th style={thStyle}>Units</th><th style={thStyle}>Revenue</th>
                            <th style={thStyle}>Cum %</th><th style={thStyle}>Class</th>
                        </tr></thead>
                        <tbody>
                            {data.rows.slice(0,80).map((r,i) => {
                                const col = { A:'#10b981', B:'#f59e0b', C:'#ef4444' }[r.class];
                                return (
                                    <tr key={i}>
                                        <td style={tdStyle}>{i+1}</td>
                                        <td style={tdStyle}><strong>{r.name}</strong></td>
                                        <td style={tdStyle}>{r.count}</td>
                                        <td style={tdStyle}>{fmtINR(r.revenue)}</td>
                                        <td style={tdStyle}>{r.cumPct}%</td>
                                        <td style={tdStyle}><span style={{padding:'2px 12px', borderRadius:'10px', backgroundColor:col+'22', color:col, fontWeight:'700'}}>{r.class}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </>)}
        </div>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────
const Kpi = ({ l, v, c = theme.navy }) => (
    <div style={statCard}>
        <span style={{fontSize:'0.74rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{l}</span>
        <h3 style={{margin:'4px 0 0', color:c, fontSize:'1.1rem', fontWeight:'700'}}>{v}</h3>
    </div>
);

const DateBar = ({ from, to, setFrom, setTo, onRun }) => (
    <div style={{display:'flex', alignItems:'end', gap:'10px', marginBottom:'14px'}}>
        <div><label style={labelStyle}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{...inputStyle, width:'150px', marginBottom:0}}/></div>
        <div><label style={labelStyle}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{...inputStyle, width:'150px', marginBottom:0}}/></div>
        <button onClick={onRun} style={{...btnStyle, marginTop:0, padding:'8px 16px', fontSize:'0.85rem'}}>Refresh</button>
    </div>
);
